import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Lock, Sparkles, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';

// ── Copy registry ────────────────────────────────────────────────────────────

const TRIGGERS = {
  budget_period_expired: {
    copy: "Ta période budgétaire est terminée. Continue ton pilotage financier avec Solo, ou garde l'accès en lecture seule à tes données.",
    plan: "Solo",
  },
  patrimony_slot_limit: {
    copy: "Tu as atteint la limite de 2 cases d'actifs du plan Gratuit. Passe à Solo ou Famille pour débloquer les 8 cases et avoir une vue complète de ton patrimoine.",
    plan: "Solo",
  },
  patrimony_asset_expansion_limit: {
    // Kept for backward-compat with any existing references; superseded by
    // asset_creation_locked which is what the backend returns now.
    copy: "Pour ajouter de nouveaux actifs au-delà de tes 2 cases du plan Gratuit, passe à Solo ou Famille. Tes actifs existants restent éditables.",
    plan: "Solo",
  },
  asset_creation_locked: {
    copy: "Tu peux toujours modifier tes actifs existants et ajouter des transactions, mais la création de nouveaux actifs nécessite le plan Solo ou Famille. Passe à un plan supérieur pour étendre ton patrimoine.",
    plan: "Solo",
  },
  objective_limit: {
    copy: "Tu as 2 objectifs actifs. Solo t'en permet autant que nécessaire.",
    plan: "Solo",
  },
  ideal_template_limit: {
    copy: "Tu as déjà créé ton modèle de budget idéal. Le plan Gratuit en autorise un seul. Passe à Solo pour en créer autant que tu veux.",
    plan: "Solo",
  },
  ideal_template_edit_locked: {
    copy: "Tu as plus de modèles que ne le permet le plan Gratuit. Tu peux toujours les consulter, mais pour les modifier ou en créer de nouveaux, passe à Solo ou Famille.",
    plan: "Solo",
  },
  active_template_locked: {
    copy: "Tu as déjà choisi ton modèle de budget idéal éditable. Le plan Gratuit ne permet pas de changer ce choix. Passe à Solo ou Famille pour modifier tous tes modèles librement.",
    plan: "Solo",
  },
  budget_period_locked_past: {
    copy: "Cette période est clôturée. Passe à Solo ou Famille pour rouvrir l'édition de tes périodes passées.",
    plan: "Solo",
  },
  budget_period_locked_future: {
    copy: "Cette période n'a pas encore commencé. Le plan Gratuit ne permet pas la modification de périodes futures. Passe à Solo ou Famille pour planifier ton budget à l'avance.",
    plan: "Solo",
  },
  legacy_period_locked: {
    copy: "Cette période n'a pas pu être synchronisée car elle dépasse la limite du plan Gratuit. Passe à Solo pour la sauvegarder et la modifier sur tous tes appareils.",
    plan: "Solo",
  },
  reopen_period_locked: {
    copy: "Rouvrir une période clôturée fait partie des plans Solo et Famille. Le plan Gratuit ne permet pas de modifier rétroactivement les périodes clôturées.",
    plan: "Solo",
  },
  another_period_already_reopened: {
    copy: "Tu as déjà une période rouverte. Reclôture-la avant d'en ouvrir une autre.",
    plan: "Solo",
  },
  period_in_standby: {
    copy: "Cette période est temporairement en attente parce qu'une autre période est rouverte. Reclôture-la pour reprendre celle-ci.",
    plan: "Solo",
  },
  budget_period_limit: {
    copy: "Tu as déjà créé une période de budget. Le plan Gratuit en autorise une seule sur la durée de ton compte. Passe à Solo pour suivre ton budget mois après mois sans limite.",
    plan: "Solo",
  },
  ideal_vs_real: {
    copy: "Compare ce que tu voulais dépenser à ce que tu as vraiment dépensé. Inclus dans Solo et Famille.",
    plan: "Solo",
  },
  simulator_pdf_save: {
    copy: "Télécharge ce scénario en PDF pour l'archiver ou le partager. Inclus dans Solo et Famille.",
    plan: "Solo",
  },
  document_storage_full: {
    copy: "Ton espace est plein. Solo passe à 25 documents, Famille à 100.",
    plan: "Solo",
  },
  partner_invite: {
    copy: "Inviter ton conjoint fait partie du plan Famille — parce qu'à deux, on pilote mieux.",
    plan: "Famille",
  },
  fictitious_member: {
    copy: "Créer un membre fictif (enfant, parent à charge) fait partie du plan Famille.",
    plan: "Famille",
  },
  household_max_adults_reached: {
    copy: "Le plan Famille couvre jusqu'à 2 adultes. Pour les enfants et personnes à charge, utilise les membres fictifs.",
    plan: "Famille",
  },
  family_readonly: {
    copy: "Cette donnée a été créée pendant ton essai. Passe en Famille pour la rendre vivante à nouveau.",
    plan: "Famille",
  },
  trial_ending_soon: {
    copy: (ctx) =>
      `Ton essai se termine dans ${ctx?.days ?? '?'} jours. Sécurise ta progression avec Solo ou Famille.`,
    plan: "Solo",
  },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function resolveCopy(trigger, userContext) {
  return typeof trigger.copy === 'function' ? trigger.copy(userContext) : trigger.copy;
}

// ── Variants ─────────────────────────────────────────────────────────────────

function ModalVariant({ copy, onUpgrade, onDismiss }) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onDismiss(); }}>
      <DialogContent
        className="bg-white border-0 max-w-sm p-0 overflow-hidden shadow-2xl"
        // DialogContent already provides focus trap + Escape via Radix
      >
        {/* Navy accent bar */}
        <div className="h-1.5 w-full bg-[#1a2b4a]" />

        <div className="px-8 pt-7 pb-8">
          {/* Icon */}
          <div className="flex justify-center mb-5">
            <div className="w-12 h-12 rounded-full bg-[#1a2b4a]/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#1a2b4a]" aria-hidden="true" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-center text-[1.1rem] font-semibold text-[#1a2b4a] mb-2 leading-snug">
            Tu mérites plus
          </h2>

          {/* Copy */}
          <p className="text-center text-sm text-zinc-500 leading-relaxed mb-8">
            {copy}
          </p>

          {/* Equal-weight buttons in a flex row — no dark pattern */}
          <div className="flex gap-3">
            <button
              onClick={onUpgrade}
              className="flex-1 py-2.5 px-4 rounded-lg bg-[#1a2b4a] text-white text-sm font-medium
                         hover:bg-[#243a63] transition-colors
                         focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a2b4a]"
            >
              Voir les plans
            </button>
            <button
              onClick={onDismiss}
              className="flex-1 py-2.5 px-4 rounded-lg border-2 border-[#1a2b4a] text-[#1a2b4a] text-sm font-medium
                         hover:bg-[#1a2b4a]/5 transition-colors
                         focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a2b4a]"
            >
              Plus tard
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BannerVariant({ copy, onUpgrade, onDismiss }) {
  return (
    <div
      role="region"
      aria-label="Invitation à découvrir un plan supérieur"
      className="flex items-center gap-3 px-4 py-2.5 bg-[#c9a961]/10 border border-[#c9a961]/40 rounded-lg"
    >
      <Lock className="w-4 h-4 shrink-0 text-[#1a2b4a]" aria-hidden="true" />

      <p className="flex-1 text-sm text-zinc-800 leading-snug">
        {copy}
      </p>

      <button
        onClick={onUpgrade}
        className="shrink-0 flex items-center gap-1 text-sm font-semibold text-[#1a2b4a]
                   hover:underline whitespace-nowrap
                   focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a2b4a]"
        aria-label="Débloquer cette fonctionnalité"
      >
        Débloquer <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
      </button>

      <button
        onClick={onDismiss}
        className="shrink-0 p-1 rounded text-zinc-400 hover:text-zinc-700 transition-colors
                   focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a2b4a]"
        aria-label="Fermer"
      >
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}

function InlineVariant({ copy, planLabel, onUpgrade }) {
  return (
    <div
      role="region"
      aria-label="Fonctionnalité verrouillée"
      className="flex flex-col items-center gap-4 py-10 px-6 bg-zinc-900/60 border border-zinc-800 rounded-xl text-center"
    >
      <div className="w-11 h-11 rounded-full bg-[#1a2b4a]/30 border border-[#1a2b4a]/50 flex items-center justify-center">
        <Lock className="w-5 h-5 text-[#c9a961]" aria-hidden="true" />
      </div>

      <p className="text-sm text-zinc-300 leading-relaxed max-w-xs">
        {copy}
      </p>

      <button
        onClick={onUpgrade}
        className="py-2 px-5 rounded-lg bg-[#1a2b4a] text-white text-sm font-medium
                   hover:bg-[#243a63] transition-colors
                   focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      >
        Débloquer avec {planLabel}
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function UpgradePrompt({
  triggerId,
  variant = 'modal',
  userContext = {},
  onDismiss,
  onUpgradeClick,
}) {
  const navigate = useNavigate();

  // Hooks must be called unconditionally — early return comes after
  // Dismissal behavior is managed by UpgradePromptContext (no localStorage here).
  const handleDismiss = useCallback(() => {
    onDismiss?.();
  }, [onDismiss]);

  const handleUpgrade = useCallback(() => {
    // Close the modal FIRST so the React state is clean before navigation.
    // Otherwise the prompt remains mounted in the background and reappears
    // when the user comes back via browser-back from /pricing.
    onDismiss?.();
    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      navigate(`/pricing?source=${triggerId}`);
    }
  }, [triggerId, onUpgradeClick, onDismiss, navigate]);

  const trigger = TRIGGERS[triggerId];
  if (!trigger) return null;

  const copy = resolveCopy(trigger, userContext);

  if (variant === 'banner') {
    return <BannerVariant copy={copy} onUpgrade={handleUpgrade} onDismiss={handleDismiss} />;
  }

  if (variant === 'inline') {
    return <InlineVariant copy={copy} planLabel={trigger.plan} onUpgrade={handleUpgrade} />;
  }

  return <ModalVariant copy={copy} onUpgrade={handleUpgrade} onDismiss={handleDismiss} />;
}

export { TRIGGERS };
